import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  useToast 
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
// import SubmitButton from '../../../CommonComponents/SubmitButton';
import { transformData } from '../../../util/helper';
import { getPersonalityTeachingData, updatePersonalityTeachingData } from '../../../services/Personality';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { personalityFormPlaceholders } from '../../../constants/constants';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const TeachingInfra = () => {
  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();


  const schema =  {
  "title": "Personality Teaching Infra Fields",
  "type": "object",
  "properties": {
    "teaching_infrastructure": {
      "title": "Teaching Infrastructure",
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "post": { "type": "string", "title": "Post/Position" },
          "data": { "type": "string", "title": "Institution/Company Name" },
          "display_date": { "type": "string", "title": "Year" },
          "image_alias": { "type": "string", "title": "Image Alias" },
          "alias": { "type": "string", "title": "URL Alias" }
        },
        "required": ["post", "data", "display_date"]
      }
    }
  }
  }

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "teaching_infrastructure": { 
      "ui:emptyValue": "",
      "items": {
        "post": {
          "ui:placeholder": personalityFormPlaceholders.TEACHING_INFRA_POST
        },
        "data": {
          "ui:placeholder": personalityFormPlaceholders.TEACHING_INFRA_INSTITUTION
        },
        "display_date": {
          "ui:placeholder": personalityFormPlaceholders.TEACHING_INFRA_YEAR
        },
        "image_alias": {
          "ui:placeholder": personalityFormPlaceholders.TEACHING_INFRA_IMAGE_ALIAS
        },
        "alias": {
          "ui:placeholder": personalityFormPlaceholders.TEACHING_INFRA_URL_ALIAS
        }
      }
    }
  };

  const formData = processedData;

  // Function to identify changed items
  const getChangedItems = (currentData, originalData) => {
    if (!currentData.teaching_infrastructure || !originalData.teaching_infrastructure) return [];
    
    const changedItems = [];
    const currentInfra = currentData.teaching_infrastructure;
    const originalInfra = originalData.teaching_infrastructure;

    // Create a map of original items by id for efficient lookup
    const originalMap = new Map();
    originalInfra.forEach(item => {
      if (item.id) {
        originalMap.set(item.id, item);
      }
    });

    // Check current items against original
    currentInfra.forEach(currentItem => {
      if (currentItem.id && originalMap.has(currentItem.id)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.id);
        
        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });
        
        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }
      } else {
        // No id or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalInfra.forEach(originalItem => {
      const existsInCurrent = currentInfra.some(currentItem => currentItem.id === originalItem.id);
      
      if (!existsInCurrent) {
        // This item doesn't exist in current data - it's deleted
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });
    
    return changedItems;
  };

  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getPersonalityTeachingData(alias);
          if (result) {

            const fieldsToTransform = [
              'poster_image_carousel_alias', 
              'other_publicity_image_carousel_alias'
            ];

            const transformedData = transformData(result, fieldsToTransform);
            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);
        
        // Get only the changed items
        const changedItems = getChangedItems(formData, originalData);
        
        if (changedItems.length > 0) {
          // Send only the changed items
          const payload = { teaching_infrastructure: changedItems };
          const result = await updatePersonalityTeachingData(alias, payload);
          if (result?.status === 200) {
            // Don't update originalData here - let it stay as the baseline
            // Only update processedData to reflect the current form state
            setProcessedData(formData);
          setQuickSave(prev => !prev);
                        showSuccessToast(toast, "Personalia data updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating teaching infrastructure data:', error);
            showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form schema={schema} uiSchema={uiSchema} formData={formData} validator={validator} onSubmit={(e) => handleSubmit(e.formData)} />
      )}
    </Box>
  );
};

export default TeachingInfra;